import * as React from 'react'

import type { ComposerSheetSnap } from './WorkflowComposer.store'

// Gap between the sheet/action bar's top edge and the markup toolbar above it.
const TOOLBAR_GAP_PX = 8

// ~5 still frames outlasts transition tail frames and iOS's stepwise keyboard
// scroll-restore without feeling laggy.
const SHEET_SETTLE_FRAMES = 5

interface IComposerLike {
  snap: ComposerSheetSnap
  centerPinInView(sheetTopY: number): void
}

// Single per-frame observer of the sheet geometry feeding both consumers: the
// --markup-toolbar-bottom var and pin re-framing on each newly settled top.
// Polling, not events — iOS's scroll-mode keyboard moves the sheet without
// firing any viewport event.
export function useComposerSheetFrame(
  store: IComposerLike,
  sheetRef: React.RefObject<HTMLElement>,
  actionBarRef: React.RefObject<HTMLElement> | null,
  isDragging: boolean,
): void {
  const isDraggingRef = React.useRef(isDragging)
  isDraggingRef.current = isDragging
  React.useEffect(() => {
    let raf = 0
    let lastBottom = -1
    let prevTop = -1
    let stableFrames = 0
    let settledTop = -1
    const tick = (): void => {
      raf = requestAnimationFrame(tick)
      const sheetRect = sheetRef.current?.getBoundingClientRect()
      const barRect = actionBarRef?.current?.getBoundingClientRect()
      const above = (rect?: DOMRect): number =>
        rect ? window.innerHeight - rect.top + TOOLBAR_GAP_PX : 0
      const bottom = Math.round(
        Math.max(TOOLBAR_GAP_PX, above(sheetRect), above(barRect)),
      )
      if (bottom !== lastBottom) {
        lastBottom = bottom
        document.documentElement.style.setProperty(
          '--markup-toolbar-bottom',
          `${bottom}px`,
        )
      }
      const top = sheetRect?.top ?? -1
      if (top < 0 || top !== prevTop) {
        prevTop = top
        stableFrames = 0
        return
      }
      stableFrames += 1
      // Fire exactly once per settle, the frame the threshold is reached.
      if (stableFrames !== SHEET_SETTLE_FRAMES) return
      if (isDraggingRef.current || store.snap === 'collapsed') {
        settledTop = -1
        return
      }
      if (top === settledTop) return
      settledTop = top
      store.centerPinInView(top)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      document.documentElement.style.removeProperty('--markup-toolbar-bottom')
    }
  }, [store, sheetRef, actionBarRef])
}
